import { DailyProgress, DeliveryStatistics, Maybe } from 'core/graphql/types';

export interface DailyProgressItemProps {
  dailyProgress: DailyProgress;
  deliveryStatistics: Maybe<DeliveryStatistics[]> | undefined;
}

const DailyProgressItem = ({
  dailyProgress,
  deliveryStatistics,
}: DailyProgressItemProps) => {
  let unit = 0;
  let exceddedEtaPercentage = 0;

  if (deliveryStatistics && deliveryStatistics[0]?.noOfDeliveriesPastEta) {
    unit =
      100 /
      (dailyProgress.totalDeliveries +
        deliveryStatistics[0].noOfDeliveriesPastEta!);
    exceddedEtaPercentage = unit * deliveryStatistics[0].noOfDeliveriesPastEta!;
  } else {
    unit = 100 / dailyProgress.totalDeliveries;
    exceddedEtaPercentage = 0;
  }

  const finishedPercentage =
    ((dailyProgress.finishedDeliveries +
      dailyProgress.cancelledDeliveries +
      dailyProgress.finishedPickups +
      dailyProgress.r30Retours +
      dailyProgress.finishedLockerVisits +
      dailyProgress.finishedPudoVisits +
      dailyProgress.finishedC2CPickups) /
      dailyProgress.totalDeliveries) *
    100;

  const finishedDeliveriesPercentage = unit * dailyProgress.finishedDeliveries;
  const cancelledDeliveriesPercentage =
    unit * dailyProgress.cancelledDeliveries;
  const finishedPickupsPercentage = unit * dailyProgress.finishedPickups;
  const r30RetoursPercentage = unit * dailyProgress.r30Retours;
  const finishedLockerVisitsPercentage = unit * dailyProgress.finishedLockerVisits;
  const finishedPudoVisitsPercentage = unit * dailyProgress.finishedPudoVisits;
  const finishedC2CPickupsPercentage = unit * dailyProgress.finishedC2CPickups;

  return (
    <div className="mt-5 flex flex-col">
      <div className="mt-2 flex h-6 overflow-hidden rounded-sm bg-white dark:bg-design-secondary-black">
        <div
          style={{ width: `${finishedDeliveriesPercentage}%` }}
          className="h-6 bg-design-green shadow-none"
        />
        <div
          style={{ width: `${cancelledDeliveriesPercentage}%` }}
          className="h-6 bg-yellow shadow-none"
        />
        <div
          style={{ width: `${finishedPickupsPercentage}%` }}
          className="h-6 bg-magenta shadow-none"
        />
        <div
          style={{ width: `${r30RetoursPercentage}%` }}
          className="h-6 bg-pink shadow-none"
        />
        <div
          style={{ width: `${finishedLockerVisitsPercentage}%` }}
          className="h-6 bg-bright-blue shadow-none"
        />
        <div
          style={{ width: `${finishedPudoVisitsPercentage}%` }}
          className="h-6 bg-orange shadow-none"
        />
        <div
          style={{ width: `${finishedC2CPickupsPercentage}%` }}
          className="h-6 bg-dark-blue shadow-none"
        />
        <div
          style={{ width: `${exceddedEtaPercentage}%` }}
          className="h-6 bg-design-red shadow-none"
        />
        <span className="mr-1 ml-auto dark:text-muted-white">{`${
          Math.round(finishedPercentage * 100) / 100
        } %`}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
        <div className="flex items-center gap-2">
          <svg
            width="8"
            height="8"
            viewBox="0 0 8 8"
            className="fill-design-green"
          >
            <path d="M4 8C6.20914 8 8 6.20914 8 4C8 1.79086 6.20914 0 4 0C1.79086 0 0 1.79086 0 4C0 6.20914 1.79086 8 4 8Z" />
          </svg>
          <span>{`${dailyProgress.finishedDeliveries} delivered`}</span>
        </div>
        <div className="flex items-center gap-2">
          <svg width="8" height="8" viewBox="0 0 8 8" className="fill-yellow">
            <path d="M4 8C6.20914 8 8 6.20914 8 4C8 1.79086 6.20914 0 4 0C1.79086 0 0 1.79086 0 4C0 6.20914 1.79086 8 4 8Z" />
          </svg>
          <span>{`${dailyProgress.cancelledDeliveries} canceled`}</span>
        </div>
        <div className="flex items-center gap-2">
          <svg width="8" height="8" viewBox="0 0 8 8" className="fill-magenta">
            <path d="M4 8C6.20914 8 8 6.20914 8 4C8 1.79086 6.20914 0 4 0C1.79086 0 0 1.79086 0 4C0 6.20914 1.79086 8 4 8Z" />
          </svg>
          <span>{`${dailyProgress.finishedPickups} pickups`}</span>
        </div>
        <div className="flex items-center gap-2">
          <svg width="8" height="8" viewBox="0 0 8 8" className="fill-magenta">
            <path d="M4 8C6.20914 8 8 6.20914 8 4C8 1.79086 6.20914 0 4 0C1.79086 0 0 1.79086 0 4C0 6.20914 1.79086 8 4 8Z" />
          </svg>
          <span>{`${dailyProgress.r30Retours} retours`}</span>
        </div>
        <div className="flex items-center gap-2">
          <svg width="8" height="8" viewBox="0 0 8 8" className="fill-bright-blue">
            <path d="M4 8C6.20914 8 8 6.20914 8 4C8 1.79086 6.20914 0 4 0C1.79086 0 0 1.79086 0 4C0 6.20914 1.79086 8 4 8Z" />
          </svg>
          <span>{`${dailyProgress.finishedLockerVisits} locker visits`}</span>
        </div>
        <div className="flex items-center gap-2">
          <svg width="8" height="8" viewBox="0 0 8 8" className="fill-orange">
            <path d="M4 8C6.20914 8 8 6.20914 8 4C8 1.79086 6.20914 0 4 0C1.79086 0 0 1.79086 0 4C0 6.20914 1.79086 8 4 8Z" />
          </svg>
          <span>{`${dailyProgress.finishedPudoVisits} PUDO visits`}</span>
        </div>
        <div className="flex items-center gap-2">
          <svg width="8" height="8" viewBox="0 0 8 8" className="fill-dark-blue">
            <path d="M4 8C6.20914 8 8 6.20914 8 4C8 1.79086 6.20914 0 4 0C1.79086 0 0 1.79086 0 4C0 6.20914 1.79086 8 4 8Z" />
          </svg>
          <span>{`${dailyProgress.finishedC2CPickups} C2C pickups`}</span>
        </div>

        {!!deliveryStatistics &&
          !!deliveryStatistics[0]?.noOfDeliveriesPastEta && (
            <div className="flex items-center gap-2">
              <svg width="12" height="11" viewBox="0 0 12 11" fill="none">
                <path
                  d="M11.3057 8.98387L6.32168 0.356873C6.25862 0.248315 6.16817 0.158217 6.05936 0.0955887C5.95056 0.0329601 5.82722 0 5.70168 0C5.57614 0 5.45279 0.0329601 5.34399 0.0955887C5.23518 0.158217 5.14473 0.248315 5.08168 0.356873L0.0976772 8.98387C0.0343421 9.09235 0.000657643 9.21558 9.53258e-06 9.34119C-0.000638578 9.46681 0.0317723 9.59038 0.0939846 9.6995C0.156197 9.80863 0.246023 9.89946 0.354445 9.96289C0.462866 10.0263 0.586065 10.0601 0.711676 10.0609H10.6807C10.8072 10.0619 10.9317 10.0295 11.0416 9.96684C11.1516 9.9042 11.2429 9.81359 11.3065 9.70421C11.3701 9.59483 11.4036 9.47058 11.4036 9.34406C11.4036 9.21755 11.3692 9.09328 11.3057 8.98387ZM5.54568 2.99887C5.69043 2.96368 5.84289 2.97935 5.97745 3.04327C6.11201 3.10718 6.22049 3.21544 6.28468 3.34988C6.33153 3.4471 6.34891 3.5559 6.33468 3.66287C6.30968 3.97587 6.29668 4.28888 6.28468 4.61488C6.25968 5.10288 6.22168 5.60387 6.19668 6.09287C6.18368 6.24287 6.18368 6.39287 6.17168 6.55587C6.16905 6.6801 6.11765 6.7983 6.02858 6.88494C5.93951 6.97158 5.81993 7.01969 5.69568 7.01888C5.57386 7.01832 5.45683 6.97137 5.36841 6.88759C5.27998 6.80381 5.22679 6.68948 5.21968 6.56787C5.18167 5.80387 5.13168 5.03987 5.09468 4.27687C5.08168 4.07687 5.06968 3.86387 5.05668 3.66287C5.05545 3.51435 5.10231 3.36942 5.19027 3.24973C5.27823 3.13005 5.40356 3.04204 5.54568 2.99887ZM5.70868 8.73387C5.62556 8.73322 5.54339 8.71615 5.46688 8.68365C5.39038 8.65114 5.32105 8.60385 5.26288 8.54447C5.20471 8.48509 5.15884 8.4148 5.12792 8.33765C5.097 8.26049 5.08162 8.17799 5.08268 8.09487C5.08321 7.92556 5.1507 7.76334 5.27042 7.64362C5.39014 7.52389 5.55237 7.4564 5.72168 7.45587C5.80571 7.4569 5.8887 7.47463 5.96582 7.50802C6.04294 7.54141 6.11265 7.58981 6.1709 7.65038C6.22915 7.71096 6.27478 7.78251 6.30512 7.86088C6.33547 7.93925 6.34994 8.02286 6.34768 8.10687C6.34014 8.2727 6.27001 8.42951 6.15143 8.54568C6.03284 8.66185 5.87463 8.72974 5.70868 8.73387Z"
                  className="fill-design-red"
                />
              </svg>
              <span>
                {`${deliveryStatistics[0]?.noOfDeliveriesPastEta} Exceeded ETA`}
              </span>
            </div>
          )}
        <div className="ml-auto flex items-center gap-2">
          <svg width="8" height="8" viewBox="0 0 8 8" className="fill-white">
            <path d="M4 8C6.20914 8 8 6.20914 8 4C8 1.79086 6.20914 0 4 0C1.79086 0 0 1.79086 0 4C0 6.20914 1.79086 8 4 8Z" />
          </svg>
          <span>{`${dailyProgress.totalDeliveries} total`}</span>
        </div>
      </div>
    </div>
  );
};
export default DailyProgressItem;
